import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { isStepConflict } from '@/lib/stepConflict';
import {
  createMarkdownComponents,
  type ResolvedLibraryImage,
} from '@/components/core/markdown/markdown';
import type { Visual } from '@/types/visual';
import { lessonApi } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { MessageSquareQuote, StickyNote, Copy } from 'lucide-react';
import { TextLessonSkeleton } from '../skeletons/PanelSkeletons';
import { TextWritingNote } from './TextWritingNote';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import type { LessonStyle } from '@/types/lesson';
import { useTextSelection } from '@/hooks/ui/useTextSelection';
import {
  SelectionPopover,
  type SelectionAction,
} from '@/components/features/learning/common/SelectionPopover';
import { useCreateNote } from '@/hooks/lesson/useNotes';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useTextSectionTime } from '@/hooks/lesson/useTextSectionTime';
import { requestChatPrefill } from '@/lib/chat-prefill';
import { citationAwareLink, type CitationMaps } from './CitationChip';
import {
  LESSON_PROSE_CODE,
  LESSON_PROSE_TYPE,
  TextLessonHeader,
  TextLessonNext,
} from './TextLessonFrame';
import { stripTitleHeading } from './stripTitleHeading';
import { stripConceptSpans } from './stripConceptSpans';
import { lessonImageRenderer } from './lessonImageRenderer';
import type { CitedByte } from '@/lib/api-client';
import type { LessonText } from '@/types/api';
import {
  pairSections,
  splitSections,
  type OpenableSection,
} from '@/lib/lesson/textSections';
import { useTextSectionParam } from '@/hooks/lesson/useTextSectionParam';
import { useReadSections } from '@/hooks/lesson/useReadSections';
import { TextContents } from './text/TextContents';
import { TextSectionBottom, TextSectionTop } from './text/TextSectionNav';
import { useTextPodcast } from './text/TextPodcast';
import { LessonMarkdown } from './text/LessonMarkdown';

interface TextRendererProps {
  lessonStyle?: LessonStyle;
  lessonId?: string;
  className?: string;
  /** Opens the lesson's Practice tab. The end-of-lesson card is shown only
   *  when this is provided. */
  onPractice?: () => void;
  /** The pipeline's text step is running. Only a record that is itself
   *  being written is shown then: a completed one is the previous version
   *  (or the moment before the step flips), not what is being written. */
  stepWriting?: boolean;
}

// Citation markers - accepts both `[[byte_id]]` (preferred) and `[byte_id]`
// (the LLM often falls back to single brackets, mirroring the `[byte_id]`
// prefix in the bytes block of the prompt).
//
// We match a *loose* UUID-shape (8-4-4-4-N where N is any 8+ hex chars) to
// also catch hallucinated byte_ids the model occasionally invents - those
// won't resolve via byteIdToIndex and get stripped silently below, leaving
// the prose clean instead of leaking literal `[[…]]` text into the page.
const UUID_LOOSE_RE =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{8,}';
const CITATION_MARKER_RE = new RegExp(
  `\\[\\[(${UUID_LOOSE_RE})\\]\\]|\\[(${UUID_LOOSE_RE})\\]`,
  'g'
);

function rewriteCitations(
  markdown: string,
  byteIdToIndex: Map<string, number>
): string {
  return markdown.replace(
    CITATION_MARKER_RE,
    (_match, doubleBracketId, singleBracketId) => {
      const byteId = doubleBracketId || singleBracketId;
      const index = byteIdToIndex.get(byteId);
      if (index === undefined) return '';
      // Encode as a markdown link so ReactMarkdown routes through the
      // custom `a` renderer, which swaps in the styled CitationChip.
      return `[${index}](cite://${byteId})`;
    }
  );
}

/** The text as written, without a code fence the model wrapped it in and
 *  without its title heading (shown by the header). */
function cleanText(content: string): string {
  let clean = content.trim();
  if (clean.startsWith('```')) {
    const lines = clean.split('\n');
    if (lines[0].startsWith('```')) lines.shift();
    if (lines.length > 0 && lines[lines.length - 1].trim() === '```') {
      lines.pop();
    }
    clean = lines.join('\n').trim();
  }
  return stripTitleHeading(clean);
}

/** How the text is read: section by section (a contents list, one section
 *  open at a time) when the lesson has two or more planned sections, else
 *  as one page. */
interface TextLayout {
  clean: string;
  intro: string;
  sections: OpenableSection[];
  sectioned: boolean;
  open: OpenableSection | null;
}

function textLayout(
  data: LessonText | undefined,
  openIndex: number | null
): TextLayout {
  const clean = data?.content ? cleanText(data.content) : '';
  const parts = splitSections(clean);
  const sections = pairSections(data?.sections ?? [], parts.sections);
  const sectioned = sections.length >= 2;
  const open =
    (sectioned &&
      sections.find(
        s => s.index === openIndex && s.ready && s.body !== null
      )) ||
    null;
  return { clean, intro: parts.intro, sections, sectioned, open };
}

/** The markdown the reading-time tracker follows: the open section (with
 *  its heading), nothing on the contents list, or the whole page. */
function trackedMarkdown(layout: TextLayout): string {
  if (!layout.sectioned) return layout.clean;
  return layout.open ? `## ${layout.open.title}\n\n${layout.open.body}` : '';
}

export function TextRenderer({
  lessonStyle: _lessonStyle,
  lessonId: propLessonId,
  className,
  onPractice,
  stepWriting = false,
}: TextRendererProps) {
  const { id: paramLessonId } = useParams<{ id: string }>();
  const lessonId = propLessonId || paramLessonId;

  // Mount/unmount activity tracking for the Text sub-tab so per-lesson
  // Coverage and study-time analytics capture this view. Errors are
  // swallowed inside the hook, so this never disrupts lesson UX.
  useActivityTracking({ contentType: 'text', lessonId });
  const {
    section: openIndex,
    open: openSection,
    close,
  } = useTextSectionParam();

  // Selection popover wiring - anchored to the markdown content container.
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Shared mermaid/code/pre handling + the citation-aware `a` and library-image
  // `img` renderers, built ONCE. Rebuilt per render, every `a`/`img` was a new
  // component type, so ReactMarkdown remounted every CitationChip on any
  // parent re-render (a poll, the chat, the tab pill) and an open hover card
  // closed under the reader's pointer. The per-response data lives in refs.
  const citationsRef = useRef<CitationMaps>({
    byId: new Map(),
    index: new Map(),
  });
  const imagesRef = useRef<Record<string, ResolvedLibraryImage>>({});
  const visualsRef = useRef<Record<string, Visual>>({});
  const markdownComponents = useMemo(
    () =>
      createMarkdownComponents({
        img: lessonImageRenderer(imagesRef, visualsRef),
        a: citationAwareLink(citationsRef),
      }),
    []
  );
  const { selection, clear: clearSelection } =
    useTextSelection(contentContainerRef);
  const createNote = useCreateNote(lessonId || '', 'selection');

  // Pull the lesson title for note auto-titling. Same query key the rest
  // of the lesson page uses, so this piggy-backs on the cached metadata
  // and never adds an extra round-trip.
  const lessonMetadataQuery = useQuery({
    queryKey: queryKeys.lessons.metadata(lessonId || ''),
    queryFn: () => lessonApi.getLesson(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
  });
  const lessonTitle = lessonMetadataQuery.data?.title || 'Lesson';

  const selectionActions: SelectionAction[] = useMemo(
    () => [
      {
        id: 'explain',
        label: 'Explain',
        icon: MessageSquareQuote,
        onClick: text => {
          requestChatPrefill(`Explain this:\n\n"${text}"`);
          notify.success('Sent to chat - edit and hit send');
        },
      },
      {
        id: 'note',
        label: 'Save to notes',
        icon: StickyNote,
        onClick: async text => {
          if (!lessonId) return;
          // "Note from {lesson} - {date} {time}". Uses the user's locale
          // so dates read naturally; dash separator matches the format
          // the user picked. Capped at 60 chars to mirror lesson-title
          // limits in case the lesson name is unusually long.
          const now = new Date();
          const stamp = now.toLocaleString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
          const title = `Note from ${lessonTitle} - ${stamp}`.slice(0, 60);
          await createNote.mutateAsync({ title, content: text });
          notify.success('Saved to notes');
        },
      },
      {
        id: 'copy',
        label: 'Copy',
        icon: Copy,
        onClick: async text => {
          await navigator.clipboard.writeText(text);
          notify.success('Copied');
        },
      },
    ],
    [createNote, lessonId, lessonTitle]
  );

  // Poll text endpoint. Continue polling not just while
  // `pending`/`processing`, but also while `completed` with no content
  // yet - the backend can flip status before flushing the artifact
  // body, which would otherwise leave us showing "No content available"
  // until a manual refresh.
  const {
    data: textData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.lessons.text(lessonId || ''),
    queryFn: () => lessonApi.getLessonText(lessonId!),
    refetchInterval: query => {
      const data = query.state.data;
      if (data?.status === 'failed') return false;
      if (data?.status === 'completed' && data.content) return false;
      return 3000;
    },
    staleTime: 0,
    retry: 3,
    enabled: !!lessonId,
  });
  const layout = useMemo(
    () => textLayout(textData, openIndex),
    [textData, openIndex]
  );
  const [readSections, setRead] = useReadSections(
    lessonId,
    layout.sections.length
  );
  // Time on each section's concepts, for Mindspace.
  useTextSectionTime(
    lessonId,
    trackedMarkdown(layout),
    scrollRef,
    contentContainerRef
  );
  // A section opens at its top. Read is the student's mark, not opening it.
  const openedIndex = layout.open?.index ?? null;
  // A lesson read as one page is one section, with one podcast.
  const podcast = useTextPodcast({
    lessonId,
    section: layout.sectioned ? openedIndex : 1,
    openSection,
  });
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [openedIndex]);

  if (!lessonId) {
    return <div className='text-red-500'>No lesson ID provided</div>;
  }

  const handleRetry = async () => {
    try {
      await lessonApi.retryStep(lessonId, 'text');
      notify.success('Retrying text generation...');
      refetch();
    } catch (err: any) {
      // 409 = nothing to retry (already generated): show the real state.
      if (isStepConflict(err)) {
        notify.info(err.detail);
        refetch();
        return;
      }
      console.error('Failed to retry text generation:', err);
      notify.error(err.message || 'Failed to retry text generation');
    }
  };

  // Initial-load and in-flight states fall through to the same panel
  // skeleton ArtifactGate uses above us. This avoids the
  // skeleton → spinner → content flicker the renderer used to introduce
  // by rendering its own `Loader2` between the gate's skeleton and the
  // final content. The gate is responsible for the status pill -
  // we just show the shape.
  if (isLoading || !textData) {
    // ArtifactGate above already confirmed pipelineStatus.text ===
    // 'completed' before mounting us, so reaching here means the
    // artifact exists and we're just waiting on the GET - not a
    // pipeline run. Tell the user that.
    return <TextLessonSkeleton mode='fetching' />;
  }
  const writing = textData.status === 'processing';
  if (
    textData.status === 'pending' ||
    (writing && !textData.content) ||
    (stepWriting && !writing)
  ) {
    return <TextLessonSkeleton />;
  }
  // Status flipped to completed but the body isn't flushed yet -
  // keep showing the skeleton; polling above keeps trying.
  if (textData.status === 'completed' && !textData.content) {
    return <TextLessonSkeleton />;
  }

  // Failed state - ArtifactGate normally intercepts this above us via
  // the pipeline status; this branch only fires when the gate is
  // bypassed (no pipeline status threaded) or when the artifact GET
  // itself errors. Either way, surface the unified failure UI.
  if (textData.status === 'failed' || error) {
    return (
      <PanelFallback
        variant='step'
        title='Text generation failed'
        description={
          textData?.error?.message ||
          "We couldn't generate the text lesson. Retry, or reload the page if it keeps failing."
        }
        primary={{ label: 'Retry', onClick: handleRetry }}
        secondary={{
          label: 'Reload page',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  // Completed, or still being written with the first sections in.
  if ((textData.status === 'completed' || writing) && textData.content) {
    const { sectioned, open, sections } = layout;
    // Read as one page, the contents list (the intro above it), or one
    // section with its heading.
    // An open section's heading is drawn on its own, so its podcast strip
    // can sit under it, as an article's audio sits under its headline.
    const shown = !sectioned
      ? layout.clean
      : open
        ? (open.body ?? '')
        : layout.intro;
    const position = open ? sections.indexOf(open) : -1;
    const lastSection = open !== null && position === sections.length - 1;

    // Citation map: byte_id → 1-based index in cited_bytes (display order
    // matches the API's first-mention order, so we just enumerate).
    const citedBytes: CitedByte[] = textData.cited_bytes ?? [];
    const byteIdToIndex = new Map<string, number>(
      citedBytes.map((c, i) => [c.byte_id, i + 1])
    );
    const citationByByteId = new Map<string, CitedByte>(
      citedBytes.map(c => [c.byte_id, c])
    );

    // text_task still wraps key terms in `<concept id="…">…</concept>`
    // spans, but the concept-hover feature is retired - strip the wrapper
    // and keep only the inner text so the spans render invisibly.
    // Citations: rewrite `[[byte_id]]` markers into `[N](cite://byte_id)`
    // so ReactMarkdown routes them through the custom `a` renderer below.
    // Markers without a matching cited_bytes entry (internal sources or
    // missing) are stripped silently.
    const conceptRendered = rewriteCitations(
      stripConceptSpans(shown),
      byteIdToIndex
    );

    // Curated library images: resolve `img://<id>` from the response's images
    // map; forward-compat (the field may be absent until the backend ships it).
    const imagesMap =
      (textData as { images?: Record<string, ResolvedLibraryImage> })?.images ??
      {};
    // The stable renderers read these at render time (see `markdownComponents`).
    citationsRef.current = { byId: citationByByteId, index: byteIdToIndex };
    imagesRef.current = imagesMap;
    visualsRef.current = textData.concept_visuals ?? {};
    const markdown = (text: string) => (
      <LessonMarkdown components={markdownComponents}>{text}</LessonMarkdown>
    );

    return (
      // `h-full overflow-y-auto` lets long markdown scroll inside the
      // text panel. MediaViewer drops its outer `p-4` for the Text
      // tab so the scrollbar lands flush against the panel border;
      // padding lives INSIDE the scroll container (`p-4`) so the
      // content still has breathing room from the edge.
      //
      // `tabIndex` + `role='region'` + a name: lesson prose is text, so there
      // is usually nothing tabbable inside it — which means a keyboard user
      // could not scroll the LESSON ITSELF. Making the container focusable is
      // the remedy axe asks for (`scrollable-region-focusable`), and it is the
      // same fix the podcast transcript took. `no-noninteractive-tabindex` was
      // already widened to allow `region` for that one.
      <div
        ref={scrollRef}
        className='h-full overflow-y-auto px-5 py-6 sm:px-10 sm:py-8'
        tabIndex={0}
        role='region'
        aria-label='Lesson content'
      >
        <div className='mx-auto max-w-[68ch]'>
          {open ? (
            <TextSectionTop
              section={open}
              total={sections.length}
              onContents={close}
            />
          ) : (
            <TextLessonHeader
              title={lessonTitle}
              wordCount={textData.word_count}
              citedBytes={citedBytes}
            />
          )}
          {!sectioned && podcast.strip}
          <div
            ref={contentContainerRef}
            // Reading measure lives on the wrapper above so the header and the
            // end card share it. `prose-base` keeps its own 1.75 line height:
            // `prose-p:text-base` used to override it to 1.5, which set lesson
            // prose noticeably tighter than a reading view should be.
            //
            // Headings step clearly (h2 22px with a rule above, h3 17px) where
            // they previously sat within 4px of each other and of bold body
            // text. `h2 + h3` gets its gap back, and a paragraph that introduces
            // a list stays close to it instead of floating mid-way.
            className={`prose prose-base max-w-none dark:prose-invert ${LESSON_PROSE_TYPE} ${LESSON_PROSE_CODE} ${className ?? ''}`}
          >
            {open && (
              <>
                {markdown(`## ${open.title}`)}
                {podcast.strip}
              </>
            )}
            {markdown(conceptRendered)}
          </div>
          {sectioned && !open && (
            <TextContents
              sections={sections}
              read={readSections}
              onOpen={index => openSection(index)}
              onRead={setRead}
            />
          )}
          {open && (
            <TextSectionBottom
              read={readSections.has(open.index)}
              onRead={read => setRead(open.index, read)}
              previous={sections[position - 1] ?? null}
              next={sections[position + 1] ?? null}
              onOpen={index => openSection(index, 'replace')}
            />
          )}
          {writing && !open ? (
            <TextWritingNote
              done={textData.sections_done ?? 0}
              total={textData.sections_total ?? 0}
            />
          ) : (
            !writing &&
            onPractice &&
            (!sectioned || lastSection) && (
              <TextLessonNext onPractice={onPractice} />
            )
          )}
        </div>
        {podcast.dock}
        <SelectionPopover
          selection={selection}
          actions={selectionActions}
          onAfterAction={clearSelection}
        />
      </div>
    );
  }

  // Default fallback
  return (
    <div className='py-8 text-center text-muted-foreground'>
      No content available
    </div>
  );
}
